
import { IFrequency } from 'proto/protobufs';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled, { css } from 'styled-components';
import { AAdditionalColumns } from '../../atoms/additionalColumns';
import { AAdditionalFieldColumns } from '../../atoms/additionalFieldColumns';
import { AAssortments } from '../../atoms/assortment';
import { ACalculatedFields } from '../../atoms/calculatedFields';
import { ACampaigns } from '../../atoms/campaignAtom';
import { AFrequencyEventType, getAvgColorByFrequencyScore, getDaysDelay, getEmojiByFrequencyScore, getRemainingDays } from '../../atoms/global/frequency';
import { IconFromName, IconName } from '../../components_v2/dropdown/DropdownColors';
import { PanelContext } from '../../components_v2/panel/context';
import { MapPinIcon2 } from '../../styles/custom-icons/map-pin';
import { PrimaryColor, BorderColor, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { CompanyStatus } from '../client-companies/model/Model';
import { Owner } from '../orders/model/Model';
import { Evolution } from '../reports/interpretor/ReportInterpretor';
import { formatCurrency, getColorForPercentage } from '../reports/utils';
import { CHECKOUT_SUM_STEPS, DEFAULT_MAP_PIN_COLOR, handlePinColor } from './cluster';
import { PinLassoPosition } from './lasso';
import { genAdditionalColumnPinData, GenAdditionalColumnPinDataResult, getStepColors, pickProductValue } from './mapAdditionalColumns';
import { AMapProduct } from '../../atoms/product';
import { MapContext } from './MapContext';
import { CompaniesInLasso, FetchKey, MinMax, MinMaxAdditionalColumn } from './MapView';
import { InitialMapRow, MapData, Point } from './model';
import { ToolTipTd } from './style';
import MapToolTip from './toolTip';
import { MapPinWrapper } from './calendarMapView';

export const MAP_DEFAULT_PIN_SIZE = 24;

export default function MapPin(props: {
	color?: string
	borderColor?: string
	size?: string | number,
	style?: React.CSSProperties
}): JSX.Element {
	return <MapPinIcon2 {...props} size={props.size ?? MAP_DEFAULT_PIN_SIZE} color={props.color ?? DEFAULT_MAP_PIN_COLOR} borderColor={props.borderColor} />;
}

const MapDotDiv = styled.div<{ size?: string, color?: string, noDot?: boolean }>`
	${(props) => props.noDot ? `
		height: 0;
		width: 0;
	` : `
		transform: translate(-50%, -50%);
		width: ${props.size ?? '12px'};
		height: ${props.size ?? '12px'};
		background-color: color-mix(in oklab, ${props.color ?? PrimaryColor} 60%, transparent);
		border-radius: 50%;
	`}
	${({ color }) => color ? `& > svg {
		color: ${color};
	}` : ''}
	cursor: pointer;
	position: relative;
	&:hover {
		z-index: 1;
		${(props) => props.noDot ? '' : `
		width: calc(${props.size ?? '12px'} * 1.2);
		height: calc(${props.size ?? '12px'} * 1.2);
		`}
	}
`;

const MapDotPopup = styled.h6<{ popupTop?: number | string }>`
	position: relative;
	background-color: ${SurfaceColor};
	color: ${SidelyBlack};
	padding: 10px;
	margin: 0;
	border: 1px solid ${BorderColor};
	border-radius: 5px;
	text-wrap: nowrap;
	width: fit-content;
	top: ${p => typeof p.popupTop === 'number' ? `${p.popupTop}px` : (p.popupTop ?? '5px')};
`;

const MapDotPopupWrapper = styled.div`
	position: absolute;
	transform: translate(-50%);
	left: 50%;
	width: fit-content;
	height: fit-content;
	top: 100%;
	display: none;
	${MapDotDiv}:hover & {
		display: block;
	}
`;


export type MapDotType<T extends React.Key = number> = {
	latitude: number,
	longitude: number,
	id: T,
	name: string,
	color?: string,
	children?: React.ReactNode,
	popup?: React.ReactNode,
	time?: Date,
	childrenWithColor?: (color: string | undefined) => React.ReactNode,
	draggable?: boolean,
	onDragStart?: (e: React.DragEvent) => void,
	onDragEnd?: (e: React.DragEvent) => void,
};

export function MapDot<T extends React.Key>({ size, color, dot, children, name, popupTop, popup }: { size?: string, color?: string, lat: number, lng: number, dot?: MapDotType<T>, children?: React.ReactNode, name?: React.ReactNode, popupTop?: number | string, popup?: React.ReactNode }) {
	// When the dot is draggable, swallow mousedown so google-map-react's gesture
	// detector doesn't consume it before the browser can start an HTML5 drag.
	const onMouseDown = dot?.draggable
		? (e: React.MouseEvent) => { e.stopPropagation(); }
		: undefined;
	return <MapDotDiv
		size={size}
		color={color ?? dot?.color}
		noDot={!!(dot?.children ?? children)}
		draggable={dot?.draggable}
		onDragStart={dot?.onDragStart}
		onDragEnd={dot?.onDragEnd}
		onMouseDown={onMouseDown}
		style={dot?.draggable ? { cursor: 'grab' } : undefined}
	>
		{children}
		{dot?.children}
		{(name || dot?.name) && <MapDotPopupWrapper><MapDotPopup popupTop={popupTop}>{popup ?? dot?.popup ?? name ?? dot?.name}</MapDotPopup></MapDotPopupWrapper>}
	</MapDotDiv>;
}

const MapPinIconOrImageCss = css`
	position: absolute;
	left: 50%;
	width: 60%;
	height: 60%;
	border-radius: 50%;
	object-fit: cover;
	pointer-events: none;
	translate: -50%;
	top: 15%;
	display: flex;
  	justify-content: center;
   	align-items: center;
`;

const MapPinImage = styled.img`
	${MapPinIconOrImageCss}
`;

const MapPinIcon = styled.div`
	${MapPinIconOrImageCss}
	background-color: white;
`;

const isIcon = (imageString: string) => !imageString.startsWith('data:image');

function MapPinIconOrImage({ imageString, pinSize, pinColor }: {
	imageString: string | IconName,
	pinSize: number,
	pinColor: string,
}) {
	return (
		isIcon(imageString)
			? <MapPinIcon>
				<IconFromName name={imageString as IconName} color={pinColor} size={pinSize / 2} strokeWidth={2.5} />
			</MapPinIcon>
			: <MapPinImage src={imageString} />
	);
}

export function MapPinWithImage({ imageString, pinSize, pinColor }: {
	imageString: string | IconName,
	pinSize: number,
	pinColor?: string,
}) {
	return <div style={{ height: pinSize, width: pinSize }}>
		<MapPin color={pinColor} size={pinSize} />
		<MapPinIconOrImage pinColor={pinColor ?? DEFAULT_MAP_PIN_COLOR} imageString={imageString} pinSize={pinSize} />
	</div>;
}

export function MapPinComponent(props: {
	cluster: Point,
	companiesInLasso: CompaniesInLasso,
	modalOptions: { isOpen: boolean, idSelected: number },
	tabType: FetchKey,
	rangeColor: { firstRange: number, lastRange: number },
	night: boolean,
	addingToLassoOnClick: boolean,
	setIsModalRightOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setCompaniesInLasso: React.Dispatch<React.SetStateAction<CompaniesInLasso>>,
	onMapMarkerCompanyClicked: (companyId: number, noCloseCluster?: true) => void,
	mapData: MapData | undefined,
	statuses: Map<number, CompanyStatus>,
	userMap: Map<number, Owner>,
	minMaxAdditionalColumn: MinMaxAdditionalColumn[],
	minMaxCalculatedFields: MinMaxAdditionalColumn[],
	checkoutsMinMax?: MinMax
}) {
	const selectedFrequencyEventType = useRecoilValue(AFrequencyEventType);
	const { hoveredPin, setHoveredPin } = React.useContext(MapContext);
	const [hoveredEvent] = React.useContext(PanelContext).calendarViewState.hoveredEventHook;
	const { companiesInLasso, modalOptions, tabType, rangeColor, night, addingToLassoOnClick, setIsModalRightOpen, setCompaniesInLasso, onMapMarkerCompanyClicked } = props;
	const val = props.cluster.properties;
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AAdditionalFieldColumns);
	const assortments = useRecoilValue(AAssortments);
	const calculatedFields = useRecoilValue(ACalculatedFields);
	const campaigns = useRecoilValue(ACampaigns);
	const mapProduct = useRecoilValue(AMapProduct);
	const [showTooltip, setShowTooltip] = React.useState(false);

	const indexInLasso = companiesInLasso.companies.findIndex(e => e.id == val.detail.id);
	let child: React.ReactNode;
	let outsideChild: React.ReactNode = undefined;
	let size: number | undefined = undefined;
	let center: boolean | undefined = undefined;
	let pinColor: string | undefined;
	let logo: string | undefined;
	let children: React.ReactNode = undefined;
	let subTitle: string | undefined;
	if (typeof tabType === 'string') {
		switch (tabType) {
			case 'status':
				pinColor = val.color;
				subTitle = props.statuses.get(val.detail.clientStatusId ?? -1)?.name;
				break;
			case 'per_users': {
				const user = props.userMap.get(val.detail.ownerId ?? -1);
				pinColor = user?.color;
				subTitle = user?.name;
				break;
			}
			case 'checkouts_sum': {
				if (!val.detail.checkoutEvolution || val.detail.checkoutEvolution.currentSum === null || val.detail.checkoutEvolution.currentSum === undefined || !props.checkoutsMinMax) break;
				const div = (props.checkoutsMinMax.max - props.checkoutsMinMax.min) / CHECKOUT_SUM_STEPS;
				const index = Math.floor(val.detail.checkoutEvolution.currentSum / div);
				pinColor = getStepColors({ r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 }, index, CHECKOUT_SUM_STEPS);
				children = <table>
					<tr><ToolTipTd colSpan={2} center>{formatCurrency(val.detail.checkoutEvolution.currentSum ?? 0)}</ToolTipTd></tr>
					<tr><ToolTipTd><Translate id='before_last_checkout_income' />:</ToolTipTd><ToolTipTd>{formatCurrency(val.detail.checkoutEvolution.previousSum ?? 0)}</ToolTipTd></tr>
				</table>;
				break;
			}
			case 'checkouts': {
				if (!val.detail.checkoutEvolution || val.detail.checkoutEvolution.evolution === null || val.detail.checkoutEvolution.evolution === undefined) break;
				let perc = val.detail.checkoutEvolution.evolution < 0 ? -val.detail.checkoutEvolution.evolution : val.detail.checkoutEvolution.evolution;
				perc = perc > 100 ? 1 : perc / 100;
				pinColor = getColorForPercentage(perc, 1, val.detail.checkoutEvolution.evolution > 0
					? [
						{ pct: 0, color: { r: 195, g: 252, b: 221 } },
						{ pct: 1, color: { r: 5, g: 119, b: 57 } }
					]
					: [
						{ pct: 0, color: { r: 255, g: 215, b: 212 } },
						{ pct: 1, color: { r: 162, g: 21, b: 9 } }
					]);
				children = <table>
					<tr><ToolTipTd colSpan={2} center><Evolution kind='small' percentage={val.detail.checkoutEvolution.evolution / 100} /></ToolTipTd></tr>
					<tr><ToolTipTd><Translate id='last_checkout_income' />:</ToolTipTd><ToolTipTd>{formatCurrency(val.detail.checkoutEvolution.currentSum ?? 0)}</ToolTipTd></tr>
					<tr><ToolTipTd><Translate id='before_last_checkout_income' />:</ToolTipTd><ToolTipTd>{formatCurrency(val.detail.checkoutEvolution.previousSum ?? 0)}</ToolTipTd></tr>
				</table>;
				break;
			}
			case 'frequencies': {
				const frequency: IFrequency | undefined | null = val.detail.frequencies?.[selectedFrequencyEventType];
				if (frequency && frequency.score && frequency.target) {
					pinColor = getAvgColorByFrequencyScore(frequency.score);
					children = <table>
						<tr><ToolTipTd colSpan={2} center>{getEmojiByFrequencyScore(frequency.score)}</ToolTipTd></tr>
						<tr><ToolTipTd><Translate id='frequency_delay' />:</ToolTipTd><ToolTipTd>{frequency.score.toFixed(2)}</ToolTipTd></tr>
						{frequency.score >= 1 && <tr><ToolTipTd><Translate id='days_delay' />:</ToolTipTd><ToolTipTd> {getDaysDelay(frequency).toFixed()}</ToolTipTd></tr>}
						{frequency.score < 1 && <tr><ToolTipTd><Translate id='remaining_days' />:</ToolTipTd><ToolTipTd>{getRemainingDays(frequency).toFixed()}</ToolTipTd></tr>}
					</table>;
				}
			}
				break;
			default:
				pinColor = handlePinColor(val.detail, tabType, rangeColor, campaigns);
				if (val.detail.formsRange !== undefined) {
					subTitle = val.detail.formsRange.toFixed(0) + ' ' + translateToString('general.days');
				} else if (tabType === 'campaign_future') {
					if (val.detail.futureCampaignId !== undefined && val.detail.futureCampaignId !== null) {
						subTitle = campaigns[val.detail.futureCampaignId]?.name;
					}
				} else if (val.detail.campaignId !== undefined && val.detail.campaignId !== null) {
					subTitle = campaigns[val.detail.campaignId]?.name;
				}
		}
	} else if ('additionalFieldColumn' in tabType || 'calculatedFieldColumn' in tabType || 'additionalColumn' in tabType) {
		let res: GenAdditionalColumnPinDataResult | undefined = undefined;
		if ('additionalFieldColumn' in tabType) {
			const additionalColumn = additionalFieldColumns.find(afc => afc.field_id == tabType.additionalFieldColumn);
			if (additionalColumn) {
				const idString = additionalColumn.field_id.toString();
				res = genAdditionalColumnPinData(
					val.detail.additionalFieldColumnsValue?.[idString],
					additionalColumn.field_type,
					rangeColor,
					assortments,
					val.detail,
					props.minMaxAdditionalColumn,
					true,
					idString,
					undefined,
					additionalColumn.display_options,
				);
			}
		} else if ('calculatedFieldColumn' in tabType) {
			const calculatedFieldColumn = calculatedFields.find(cf => cf.id == tabType.calculatedFieldColumn);
			if (calculatedFieldColumn) {
				const idString = calculatedFieldColumn.id.toString();
				res = genAdditionalColumnPinData(
					pickProductValue(val.detail.calculatedFieldColumnsValue?.[idString], mapProduct?.uuid) ?? undefined,
					'ReportColumn',
					rangeColor,
					assortments,
					val.detail,
					props.minMaxCalculatedFields,
					false,
					idString,
					undefined,
					calculatedFieldColumn.display_options,
				);
			}
		} else {
			const additionalColumn = additionalColumns.find(ac => ac.id == tabType.additionalColumn);
			if (additionalColumn) {
				const idString = additionalColumn.id.toString();
				res = genAdditionalColumnPinData(
					val.detail.additionalColumnsValue?.[idString],
					additionalColumn.type,
					rangeColor,
					assortments,
					val.detail,
					props.minMaxAdditionalColumn,
					false,
					idString,
					additionalColumn?.display_options?.pin_styles,
					additionalColumn?.display_options,
				);
			}
		}
		if (res) {
			logo = res.logo;
			center = res.center;
			outsideChild = res.outsideChild;
			size = res.size;
			child = res.child;
			pinColor = res.color;
			children = res.children;
			subTitle = res.subTitle;

		}
	}

	if (val.detail.id == modalOptions.idSelected) {
		size = 30;
		pinColor = PrimaryColor;
		child = undefined;
		outsideChild = undefined;
		center = undefined;
	}

	if (!child && showTooltip && !outsideChild) outsideChild = <MapToolTip
		companyId={val.detail.id ?? undefined}
		instore
		title={val.detail.name!}
		night={night}
		up={!children}
		subTitle={subTitle}
	>
		{children}
	</MapToolTip>;
	if (!size) {
		size = MAP_DEFAULT_PIN_SIZE;
		if (logo) {
			size = MAP_DEFAULT_PIN_SIZE * 1.5;
		}
	}
	if (!child) child = logo
		? <MapPinWithImage pinColor={pinColor ?? DEFAULT_MAP_PIN_COLOR} imageString={logo} pinSize={size} />
		: <MapPin color={pinColor} size={size} />;
	const isSameHasHoveredEvent = hoveredEvent && hoveredEvent.companyId == val.detail.id;

	return <MapPinWrapper
		size={size}
		center={center}
		onMouseEnter={() => {
			setShowTooltip(true);
			return indexInLasso !== -1 && setHoveredPin(val.detail.id!);
		}}
		onMouseLeave={() => {
			setShowTooltip(false);
			return indexInLasso !== -1 && setHoveredPin(undefined);
		}}
	>
		<PinContainer
			onClick={() => {
				if (addingToLassoOnClick) {
					setIsModalRightOpen(true);
					setCompaniesInLasso(c => ({
						...c,
						companies: Array.from(
							new Set(
								companiesInLasso.companies.concat(
									[val.detail as InitialMapRow]
								)
							)
						)
					}));
				} else {
					onMapMarkerCompanyClicked(val.detail.id!);
				}
			}}
		>
			{child}
		</PinContainer>
		{indexInLasso !== -1 && <PinLassoPosition position={indexInLasso + 1} selected={hoveredPin === val.id || isSameHasHoveredEvent} />}
		{indexInLasso === -1 && isSameHasHoveredEvent && <PinLassoPosition selected={hoveredPin === val.id || isSameHasHoveredEvent} style={{ size: '10px' }} />}
		{outsideChild}
	</MapPinWrapper>;
}

const PinContainer = styled.div`
	height: fit-content;
	width: fit-content;
	position: relative;
	display: flex;
	cursor: pointer;
`;
